/**
 * @module AppIndex
 */
// eslint-disable-next-line no-unused-vars
import React from 'react';
import { AuthProvider } from '@lifechurch/web-tools-io/dist/context/AuthContext';
import { Log } from '@lifechurch/web-tools-io/dist/utils/helpers/browserLogger';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { createRoot } from 'react-dom/client';
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { AlgoliaSearchProvider } from './context/AlgoliaSearchContext';
import { MagnoliaProvider } from './context/MagnoliaContext';
import { ProfileProvider } from './context/ProfileContext';
import Authentication from './views/Authentication';
import packageJson from '../package.json';
import {
  APP_CONFIG,
  LOG_CONFIG,
  loadNewRelicBrowserMonitor,
  loadSegment,
} from './utils';
import App from './App';

Log.configure(LOG_CONFIG);

const container = document.getElementById('root');
const root = createRoot(container);
const basePath = '/lifegroups/search';

if (!Bugsnag.isStarted()) {
  Bugsnag.start({
    apiKey: process.env.BUGSNAG_API_KEY,
    appVersion: packageJson.version,
    // eslint-disable-next-line no-unused-vars
    onUnhandledRejection: (err, event) => {
      if (err instanceof Error) {
        Bugsnag.notify(err);
      }
    },
    plugins: [new BugsnagPluginReact()],
    releaseStage: process.env.NODE_ENV,
  });
}
if (process.env.SEGMENT_KEY && process.env.SEGMENT_URL) {
  /**
   * Fetch user from local storage to pass along to Segment for analytics.
   */
  const user = JSON.parse(window?.localStorage?.getItem('user_profile'));
  const pagePayload = {
    category: document?.title?.split('|')?.[0]?.trim() || '',
    name: document?.title || '',
    properties: {
      logged_in: !!user,
      path: window?.location?.pathname,
      preferred_campus: null,
      referrer: document?.referrer || null,
      title: document?.title || '',
      url: window?.location?.href,
      user_id: user?.['https://www.life.church/rock_person_alias_id'],
    },
  };
  loadSegment({ autoCallPage: false, autoLoad: true, pagePayload });
}

const nrMonitoringScript = document.querySelector('script[data-monitoring-nr]');
if (
  Boolean(process.env.NEW_RELIC_BROWSER_MONITOR_ENABLE) &&
  !nrMonitoringScript?.length
) {
  nrMonitoringScript.innerHTML = loadNewRelicBrowserMonitor();
}

/**
 * Note: The `?` at the end of paths is the optional segment flag. This means
 * that a path `/path?/help` will load the element for both `/path/help` as well
 * as simply `/help`, since they are both treated the same.
 *
 * Source: https://reactrouter.com/en/main/route/route#optional-segments.
 */
const router = createBrowserRouter([
  {
    element: (
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    ),
    path: '/',
  },
  {
    element: (
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    ),
    path: basePath,
  },
  {
    element: (
      <ErrorBoundary>
        <App view={APP_CONFIG.appViews.lgDetailPage} />
      </ErrorBoundary>
    ),
    path: '/lifegroup/:groupId',
  },
  {
    element: (
      <ErrorBoundary>
        <App view={APP_CONFIG.appViews.lgDetailPage} />
      </ErrorBoundary>
    ),
    path: `${basePath}/lifegroup/:groupId`,
  },
  {
    element: (
      <ErrorBoundary>
        <Authentication />
      </ErrorBoundary>
    ),
    path: `/auth/auth0/callback/`,
  },
  {
    element: (
      <ErrorBoundary>
        <Navigate replace="true" to={basePath} />
      </ErrorBoundary>
    ),
    path: '*',
  },
]);

root.render(
  <AuthProvider>
    <ProfileProvider>
      <MagnoliaProvider>
        <AlgoliaSearchProvider>
          <RouterProvider router={router}></RouterProvider>
        </AlgoliaSearchProvider>
      </MagnoliaProvider>
    </ProfileProvider>
  </AuthProvider>,
);
